import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionBarProvider } from "@air/provider-action-bar";
import { ModalProvider } from "@air/provider-modal";
import { ShortIdProvider } from "@air/provider-short-id";
import { ErrorBoundary } from "@air/utils-error";
import { ConnectedRouter } from "connected-next-router";
import { useRouter } from "next/router";
import { memo, useCallback } from "react";
import { AccountProvisionerProvider } from "~/components/AccountProvisioner/providers/AccountProvisionerProvider";
import { MaintenanceMode } from "~/components/MaintenanceMode/MaintenanceMode";
import { PublicBoardReduxProvider } from "~/components/PublicBoardReduxProvider";
import { PublicBoardSocketContextProvider } from "~/components/PublicBoardSocketContextProvider";
import { PublicPermissionsProvider } from "~/components/PublicPermissionsProvider";
import { PublicWorkspaceProvider } from "~/components/PublicWorkspaceProvider";
import { KanbanOffsetContextProvider } from "~/components/v2/components/shared/kanban/KanbanOffsetContextProvider";
import { TableHeaderPositionContextProvider } from "~/components/v2/components/shared/table/TableHeaderPositionContextProvider";
import { PublicWorkspaceHeader } from "~/components/v2/components/workspace/workspace-header/PublicWorkspaceHeader";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { ThumbnailPreferenceContextProvider } from "~/hooks/useThumbnailPreference";
import { PublicAccountProvider } from "~/providers/AccountProvider";
import { FiltersProvider } from "~/providers/FiltersProvider";
import { ModifiersKeysPressedProvider } from "~/providers/ModifiersKeysPressedProvider";
import { PublicBoardCustomFieldsProvider } from "~/providers/PublicBoardCustomFieldsProvider";
import { SearchHeaderHeightProvider } from "~/providers/SearchHeaderHeightProvider";
import { getBoardIdFromPath } from "~/utils/PathUtils";
export const PublicDefaultLayout = /*#__PURE__*/ memo((param)=>{
    let { children, Search, headerActions, initialData, shortId } = param;
    const { ui2 } = useTemporaryFeatureFlags();
    const { asPath } = useRouter();
    const getImmutableFilters = useCallback(()=>({
            boardId: getBoardIdFromPath(asPath)
        }), [
        asPath
    ]);
    if (!ui2) return null;
    // If we have public board data, wrap with all the providers
    if (initialData && shortId) {
        const { name: workspaceName, image: workspaceImage, id: workspaceId } = initialData.workspace;
        return /*#__PURE__*/ _jsx(AccountProvisionerProvider, {
            children: /*#__PURE__*/ _jsx(PublicAccountProvider, {
                workspace: initialData.workspace,
                children: /*#__PURE__*/ _jsx(PublicPermissionsProvider, {
                    permissions: initialData.permissions,
                    children: /*#__PURE__*/ _jsx(PublicBoardReduxProvider, {
                        initialBoard: initialData.board,
                        children: /*#__PURE__*/ _jsx(ShortIdProvider, {
                            shortId: shortId,
                            objectId: initialData.board.id,
                            objectType: "board",
                            children: /*#__PURE__*/ _jsx(PublicWorkspaceProvider, {
                                workspaceId: workspaceId,
                                workspaceImage: workspaceImage,
                                workspaceName: workspaceName,
                                children: /*#__PURE__*/ _jsx(ErrorBoundary, {
                                    children: /*#__PURE__*/ _jsx(ConnectedRouter, {
                                        children: /*#__PURE__*/ _jsx(SearchHeaderHeightProvider, {
                                            children: /*#__PURE__*/ _jsx(FiltersProvider, {
                                                getImmutableFilters: getImmutableFilters,
                                                children: /*#__PURE__*/ _jsx(ModifiersKeysPressedProvider, {
                                                    children: /*#__PURE__*/ _jsx(ModalProvider, {
                                                        children: /*#__PURE__*/ _jsx(ThumbnailPreferenceContextProvider, {
                                                            children: /*#__PURE__*/ _jsx(MaintenanceMode, {
                                                                children: /*#__PURE__*/ _jsx(PublicBoardCustomFieldsProvider, {
                                                                    children: /*#__PURE__*/ _jsx(PublicBoardSocketContextProvider, {
                                                                        children: /*#__PURE__*/ _jsx(ActionBarProvider, {
                                                                            children: /*#__PURE__*/ _jsx(TableHeaderPositionContextProvider, {
                                                                                children: /*#__PURE__*/ _jsx(KanbanOffsetContextProvider, {
                                                                                    children: /*#__PURE__*/ _jsxs("div", {
                                                                                        className: "flex size-full h-screen flex-col overflow-hidden bg-grey-4",
                                                                                        children: [
                                                                                            /*#__PURE__*/ _jsx(PublicWorkspaceHeader, {
                                                                                                Search: Search,
                                                                                                headerActions: headerActions
                                                                                            }),
                                                                                            /*#__PURE__*/ _jsx("div", {
                                                                                                className: "flex grow gap-2 overflow-hidden px-2 pb-2",
                                                                                                children: children
                                                                                            })
                                                                                        ]
                                                                                    })
                                                                                })
                                                                            })
                                                                        })
                                                                    })
                                                                })
                                                            })
                                                        })
                                                    })
                                                })
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        });
    }
    // Fallback for non-board public content
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
});
PublicDefaultLayout.displayName = "PublicDefaultLayout";
export const getPublicDefaultLayout = (page)=>/*#__PURE__*/ _jsx(PublicDefaultLayout, {
        children: page
    });
